import {apiSlice} from './apiSlice';

/**
 * Gói & Hạn mức của chính user (features/Subscription).
 *
 * - getMySubscription: gói hiện tại + thanh tiến trình (token/tháng, URL/tuần, dự án).
 *   Gắn tag 'Subscription' để tự refetch sau khi bị 402 hoặc khi admin đổi gói.
 * - getPlans: danh sách gói đang mở bán (bảng so sánh + nâng cấp).
 */
export const subscriptionApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getMySubscription: builder.query({
			query: () => ({url: 'subscription/me', method: 'get'}),
			transformResponse: (body) => body?.data || null,
			providesTags: ['Subscription'],
		}),
		getPlans: builder.query({
			query: () => ({url: 'subscription/plans', method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: ['Subscription'],
		}),
		// Lịch sử cộng/trừ token của chính user. Gắn tag 'Subscription' để tự refetch
		// sau mỗi lượt tốn token (mutation AI invalidate 'Subscription').
		getMyTokenLedger: builder.query({
			query: () => ({url: 'subscription/ledger', method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: ['Subscription'],
		}),
		// Yêu cầu nâng cấp gói của chính user (null nếu chưa từng gửi).
		getMyUpgradeRequest: builder.query({
			query: () => ({url: 'subscription/upgrade-request', method: 'get'}),
			transformResponse: (body) => body?.data ?? null,
			providesTags: ['PlanRequest'],
		}),
		requestUpgrade: builder.mutation({
			query: (body) => ({url: 'subscription/upgrade-request', method: 'post', data: body}),
			invalidatesTags: ['PlanRequest'],
		}),
		// Lịch sử đơn hàng gói của chính user (50 đơn gần nhất, mọi trạng thái). Tag 'PlanRequest'
		// để tự refetch sau khi tạo đơn / "Tôi đã thanh toán" / huỷ đơn.
		getMyOrders: builder.query({
			query: () => ({url: 'subscription/orders', method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: ['PlanRequest'],
		}),
		// Thanh toán thủ công VietQR: khách bấm "Tôi đã thanh toán" / huỷ đơn pending.
		confirmUpgradePaid: builder.mutation({
			query: () => ({url: 'subscription/upgrade-request/paid', method: 'post'}),
			invalidatesTags: ['PlanRequest'],
		}),
		cancelUpgradeRequest: builder.mutation({
			query: () => ({url: 'subscription/upgrade-request/cancel', method: 'post'}),
			invalidatesTags: ['PlanRequest'],
		}),
		// Hạ gói CÓ LỊCH: gói hiện tại dùng tiếp tới hết hạn rồi mới chuyển sang gói thấp hơn
		// (subscription/me trả billing.next_plan); huỷ quyết định được trước mốc đó.
		// Invalidate cả 'PlanRequest' vì huỷ lịch có thể huỷ kèm đơn thanh toán gói đích.
		scheduleDowngrade: builder.mutation({
			query: (body) => ({url: 'subscription/downgrade', method: 'post', data: body}),
			invalidatesTags: ['Subscription', 'PlanRequest'],
		}),
		cancelDowngrade: builder.mutation({
			query: () => ({url: 'subscription/downgrade/cancel', method: 'post'}),
			invalidatesTags: ['Subscription', 'PlanRequest'],
		}),
		// Gói Extra — pack đang bán + đơn mua của chính user (mua thêm hạn mức, admin duyệt).
		getExtraPacks: builder.query({
			query: () => ({url: 'subscription/extra-packs', method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: ['ExtraPack'],
		}),
		getMyExtraPurchases: builder.query({
			query: () => ({url: 'subscription/extra-purchases', method: 'get'}),
			transformResponse: (body) => body?.data || {items: [], pending: null, extra_this_month: 0},
			providesTags: ['ExtraPurchase'],
		}),
		requestExtraPurchase: builder.mutation({
			query: (body) => ({url: 'subscription/extra-purchases', method: 'post', data: body}),
			invalidatesTags: ['ExtraPurchase'],
		}),
	}),
});

export const {
	useGetMySubscriptionQuery,
	useGetPlansQuery,
	useGetMyTokenLedgerQuery,
	useGetMyUpgradeRequestQuery,
	useRequestUpgradeMutation,
	useGetMyOrdersQuery,
	useConfirmUpgradePaidMutation,
	useCancelUpgradeRequestMutation,
	useScheduleDowngradeMutation,
	useCancelDowngradeMutation,
	useGetExtraPacksQuery,
	useGetMyExtraPurchasesQuery,
	useRequestExtraPurchaseMutation,
} = subscriptionApiSlice;
